import type { FC } from 'react';
import { useEffect, useState } from 'react';

import { useAtom } from 'jotai';

import { shouldAllowRecordingAtom } from '../../atoms';

import { LargeMicVisualizer } from './LargeMicVisualizer';
import { MicLoadingState } from './MicLoadingState';
import MiniMicVisualizer from './MiniMicVisualizer';

type VoiceCardProps = {
  stream: MediaStream | null;
  onMuteStateChange?: (isMuted: boolean) => void;
  onAudioActivityChange?: (hasActivity: boolean) => void;
  isRecordingSessionActive?: boolean;
};

export const VoiceCard: FC<VoiceCardProps> = ({
  stream,
  onMuteStateChange,
  onAudioActivityChange,
  isRecordingSessionActive,
}) => {
  const isStreamActive = stream?.active === true;
  const [hasAudioActivity, setHasAudioActivity] = useState(false);
  const [hasDebouncedAudioActivity, setDebouncedAudioActivity] =
    useState(false);
  const [isAudioAnalysisReady, setIsAudioAnalysisReady] = useState(false);
  const [, setShouldAllowRecording] = useAtom(shouldAllowRecordingAtom);

  // Debounce audio activity changes to prevent constant switching
  useEffect(() => {
    if (!stream) {
      setDebouncedAudioActivity(false);
      onAudioActivityChange?.(false);
      setShouldAllowRecording(false);
      return;
    }

    // If enabling, apply immediately
    if (hasAudioActivity) {
      setDebouncedAudioActivity(true);
      onAudioActivityChange?.(true);
      setShouldAllowRecording(true);
      return;
    }

    // If disabling, apply after 5 second delay
    const timeoutId = setTimeout(() => {
      setDebouncedAudioActivity(false);
      onAudioActivityChange?.(false);
      setShouldAllowRecording(false);
    }, 5000); // 5 second delay for enabling

    return () => clearTimeout(timeoutId);
  }, [
    stream,
    hasAudioActivity,
    onAudioActivityChange,
    setShouldAllowRecording,
  ]);

  // Audio activity detection using Web Audio API
  useEffect(() => {
    if (!stream) {
      setHasAudioActivity(false);
      setIsAudioAnalysisReady(false);
      return;
    }

    // Create audio context and analyser to process the microphone stream
    const audioContext = new AudioContext();
    const analyser = audioContext.createAnalyser();
    const microphone = audioContext.createMediaStreamSource(stream);

    // Connect microphone to analyser for real-time audio analysis
    microphone.connect(analyser);
    analyser.fftSize = 256; // Number of frequency bins (higher = more detail)
    analyser.smoothingTimeConstant = 0.8; // Smoothing factor (0-1, higher = more smoothing)

    // Array to hold frequency data from the analyser
    const dataArray = new Uint8Array(analyser.frequencyBinCount);

    let analysisCount = 0;
    const requiredAnalysisCycles = 5; // Wait for 5 analysis cycles before showing components

    const checkActivity = () => {
      // Get current frequency data from the microphone
      analyser.getByteFrequencyData(dataArray);

      // Calculate RMS (Root Mean Square) - a measure of audio signal strength
      // RMS = sqrt(sum of squared values / number of values)
      // This gives us the average "loudness" of the audio signal
      const rms = Math.sqrt(
        dataArray.reduce((sum, val) => sum + val * val, 0) / dataArray.length,
      );

      // Determine if there's significant audio activity
      // Threshold of 10 is adjustable - higher = requires louder audio to trigger
      const isActive = rms > 10;
      setHasAudioActivity(isActive);

      // Mark analysis as ready after a few cycles to prevent quick off->on transitions
      analysisCount++;
      if (analysisCount >= requiredAnalysisCycles) {
        setIsAudioAnalysisReady(true);
      }
    };

    // Check for audio activity 20 times per second (50ms intervals)
    const interval = setInterval(checkActivity, 50);

    // Cleanup: stop monitoring and close audio context when component unmounts
    return () => {
      clearInterval(interval);
      audioContext.close();
    };
  }, [stream, onAudioActivityChange]);

  return (
    <div className="vai-flex vai-flex-col vai-items-center vai-gap-1 vai-py-1">
      {/* Microphone level indicator */}
      <div className="vai-flex vai-items-center vai-justify-center">
        {!isAudioAnalysisReady ? (
          <MicLoadingState />
        ) : !isRecordingSessionActive ? (
          <LargeMicVisualizer
            isActive={isStreamActive && hasDebouncedAudioActivity}
          />
        ) : (
          <MiniMicVisualizer
            stream={stream}
            onMuteStateChange={onMuteStateChange}
            barCount={24}
          />
        )}
      </div>
    </div>
  );
};
